/**
 * The font library: families uploaded or taken from Google Fonts, shared
 * across the firm and served by our own API.
 *
 * A family's files are fetched here, with the reader's token, and handed to
 * the page as local copies (see `features/typography/libraryFonts.ts`): a
 * stylesheet pointing at this API would be fetched by the browser without the
 * token. Google Fonts is reached by the server only; nothing here talks to
 * Google.
 */

import { http, type RequestOptions } from '@/api/http'

export type FontCategory = 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace'

export const FONT_CATEGORIES: readonly FontCategory[] = [
  'sans-serif',
  'serif',
  'display',
  'handwriting',
  'monospace',
]

export interface FontStyle {
  /** The weight, or the lightest a variable font reaches. */
  readonly weight: number
  readonly italic: boolean
  /** The heaviest a variable font reaches; absent for a font of one weight. */
  readonly weightMax?: number | null
}

export interface LibraryFontFile extends FontStyle {
  readonly id: number
  /** The characters the file holds, empty for a whole font. */
  readonly unicodeRange: string
  readonly size: number
  /** The same for every style that is the same file, so it is fetched once. */
  readonly digest: string
}

export interface LibraryFamily {
  readonly id: number
  readonly name: string
  readonly category: FontCategory
  readonly source: 'upload' | 'google'
  readonly googleFamily: string | null
  readonly owner: string
  readonly files: readonly LibraryFontFile[]
}

export interface GoogleFamily {
  readonly family: string
  readonly category: FontCategory
  readonly styles: readonly FontStyle[]
  /** The library's copy of it, if it has one. */
  readonly libraryId: number | null
}

export interface GooglePage {
  readonly total: number
  readonly families: readonly GoogleFamily[]
}

interface StoredFile {
  id: number
  weight: number
  weight_max: number | null
  italic: boolean
  unicode_range: string
  size: number
  digest: string
}

interface StoredFamily {
  id: number
  name: string
  category: string
  source: string
  google_family: string | null
  owner: string
  files: StoredFile[]
}

interface StoredGoogleFamily {
  family: string
  category: string
  styles: FontStyle[]
  library_id: number | null
}

function category(value: string): FontCategory {
  return FONT_CATEGORIES.find((one) => one === value) ?? 'sans-serif'
}

function readFamily(stored: StoredFamily): LibraryFamily {
  return {
    id: stored.id,
    name: stored.name,
    category: category(stored.category),
    source: stored.source === 'google' ? 'google' : 'upload',
    googleFamily: stored.google_family,
    owner: stored.owner,
    files: stored.files.map((file) => ({
      id: file.id,
      weight: file.weight,
      weightMax: file.weight_max,
      italic: file.italic,
      unicodeRange: file.unicode_range,
      size: file.size,
      digest: file.digest,
    })),
  }
}

function filesForm(files: readonly File[], fields: Record<string, string> = {}): FormData {
  const form = new FormData()
  for (const file of files) form.append('files', file, file.name)
  for (const [key, value] of Object.entries(fields)) if (value) form.append(key, value)
  return form
}

export async function listFonts(options: RequestOptions = {}): Promise<LibraryFamily[]> {
  const { fonts } = await http.get<{ fonts: StoredFamily[] }>('/fonts', options)
  return fonts.map(readFamily)
}

/** A new family from font files; named as the first file says unless `name` is given. */
export async function uploadFonts(
  files: readonly File[],
  fields: { name?: string; category?: FontCategory },
  options: RequestOptions = {},
): Promise<LibraryFamily> {
  const form = filesForm(files, { name: fields.name ?? '', category: fields.category ?? '' })
  const { font } = await http.post<{ font: StoredFamily }>('/fonts', form, options)
  return readFamily(font)
}

export async function addFontFiles(
  id: number,
  files: readonly File[],
  options: RequestOptions = {},
): Promise<LibraryFamily> {
  const { font } = await http.post<{ font: StoredFamily }>(
    `/fonts/${id}/files`,
    filesForm(files),
    options,
  )
  return readFamily(font)
}

export async function updateFont(
  id: number,
  changes: { name?: string; category?: FontCategory },
  options: RequestOptions = {},
): Promise<LibraryFamily> {
  const { font } = await http.put<{ font: StoredFamily }>(`/fonts/${id}`, changes, options)
  return readFamily(font)
}

export async function deleteFont(id: number, options: RequestOptions = {}): Promise<boolean> {
  const { deleted } = await http.delete<{ deleted: boolean }>(`/fonts/${id}`, options)
  return deleted
}

/** One file of a family, as WOFF2. */
export async function fontFile(
  familyId: number,
  fileId: number,
  options: RequestOptions = {},
): Promise<Blob> {
  const { blob } = await http.download(`/fonts/${familyId}/files/${fileId}`, undefined, options)
  return blob
}

export async function searchGoogleFonts(
  search: { query: string; category: FontCategory | null; offset: number; limit: number },
  options: RequestOptions = {},
): Promise<GooglePage> {
  const page = await http.get<{ total: number; families: StoredGoogleFamily[] }>('/fonts/google', {
    ...options,
    query: { ...search, category: search.category ?? undefined },
  })
  return {
    total: page.total,
    families: page.families.map((one) => ({
      family: one.family,
      category: category(one.category),
      styles: one.styles,
      libraryId: one.library_id,
    })),
  }
}

/** One Latin file of a Google family, to show it in itself before it is added. */
export async function googleSample(family: string, options: RequestOptions = {}): Promise<Blob> {
  const { blob } = await http.download('/fonts/google/sample', undefined, {
    ...options,
    query: { family },
  })
  return blob
}

/**
 * A Google family, fetched by the server once. Listed, it joins the library;
 * unlisted, it only stands in for a font an imported PDF used.
 */
export async function addGoogleFont(
  family: string,
  listed: boolean,
  options: RequestOptions = {},
): Promise<LibraryFamily> {
  const { font } = await http.post<{ font: StoredFamily }>(
    '/fonts/google',
    { family, listed },
    options,
  )
  return readFamily(font)
}
